import type {ISceneManager} from "#template/interfaces/scene-manager"
import type {CurveData, CurveObjectData, MeshObjectData} from "#template/interfaces/object-data"
import {computePolygonArea} from "#template/utils/scene-geometry-utils"

export const computeCurveArea = (baseMesh: MeshObjectData, curveData: CurveData, sceneManager: ISceneManager, uvChannel: number): number => {
    const meshQueries = sceneManager.getMeshGeometryQueries()
    const {controlPoints} = curveData
    if (controlPoints.length <= 2) return 0

    const uvs = controlPoints
        .map((controlPoint) => meshQueries.projectPointToMeshInPositionSpace(baseMesh.completeMeshData.reified, controlPoint.position)?.uvs[uvChannel])
        .filter((uv) => uv != undefined)

    if (uvs.length !== controlPoints.length) throw new Error("Could not get UVs for all control points")

    return computePolygonArea(uvs)
}

export const reverseFloat32Array = (array: Float32Array, stride: number): Float32Array => {
    const result = new Float32Array(array.length)
    const n = array.length
    for (let i = 0; i < n; i += stride) {
        for (let k = 0; k < stride; k++) {
            result[i + k] = array[n - stride - i + k]
        }
    }
    return result
}

export const orderAndOrientCurves = (
    baseMesh: MeshObjectData,
    curves: CurveObjectData[],
    sceneManager: ISceneManager,
    uvChannel: number,
): CurveObjectData[] => {
    return curves
        .map((curve) => ({curve, area: computeCurveArea(baseMesh, curve, sceneManager, uvChannel)}))
        .sort((a, b) => Math.abs(b.area) - Math.abs(a.area))
        .map((curveWithSignedArea) => {
            if (curveWithSignedArea.area > 0) {
                const {
                    controlPoints,
                    completeCurveData: {reified: reifiedCurveData},
                } = curveWithSignedArea.curve

                const curveObjectData = {
                    ...curveWithSignedArea.curve,
                    controlPoints: [...controlPoints].reverse(),
                    completeCurveData: {
                        ...curveWithSignedArea.curve.completeCurveData,
                        reified: reifiedCurveData
                            ? {
                                  ...reifiedCurveData,
                                  points: reverseFloat32Array(reifiedCurveData.points, 3),
                                  normals: reverseFloat32Array(reifiedCurveData.normals, 3),
                                  tangents: reverseFloat32Array(reifiedCurveData.tangents, 3),
                                  segments: reverseFloat32Array(reifiedCurveData.segments, 1),
                                  scales: reverseFloat32Array(reifiedCurveData.scales, 1),
                              }
                            : null,
                    },
                }

                return curveObjectData
            }

            return curveWithSignedArea.curve
        })
}
